/**
 * ContactDiagnosticPanel — M2.5 P0-4C · UI Super Admin.
 *
 * Riusa l'endpoint READ-ONLY già deployato:
 *   GET /api/admin/messaging/whatsapp/contact-diagnostic
 *
 * Usa esclusivamente l'autenticazione dell'app (interceptor `api.js`).
 * NESSUNA nuova logica backend, nessun send/retry/update. Se non
 * super_admin → il componente ritorna null e la sezione non è visibile.
 *
 * Preselezione: se il componente riceve `presetConversationId`
 * (dallo Staff "Diagnostica questa conversazione"), lo popola nel form.
 */
import React, { useState, useCallback, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

function Field({ label, value, mono = false, testId }) {
  const display = (value === undefined || value === null || value === "")
    ? "—" : String(value);
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wider text-ink-500">{label}</span>
      <span data-testid={testId}
            className={`text-sm text-ink-900 break-all ${mono ? "font-mono" : ""}`}>
        {display}
      </span>
    </div>
  );
}

function Chip({ children, tone = "neutral", testId }) {
  const tones = {
    neutral: "border-ink-300 text-ink-700 bg-ink-50",
    red: "border-red-400 text-red-800 bg-red-50",
    amber: "border-amber-400 text-amber-800 bg-amber-50",
    green: "border-emerald-500 text-emerald-800 bg-emerald-50",
    blue: "border-blue-400 text-blue-800 bg-blue-50",
  };
  return (
    <span data-testid={testId}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                       border text-xs font-medium ${tones[tone] || tones.neutral}`}>
      {children}
    </span>
  );
}

function statusTone(s) {
  if (!s) return "neutral";
  if (["failed", "error"].includes(s)) return "red";
  if (s === "delivery_unknown") return "amber";
  if (["sent", "delivered", "read", "accepted"].includes(s)) return "green";
  return "neutral";
}

function fmtIso(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("it-IT"); } catch { return s; }
}

// -----------------------------------------------------------------------
// Sections renderers
// -----------------------------------------------------------------------

function IdentitySection({ identity }) {
  if (!identity) return null;
  return (
    <div data-testid="diag-identity"
         className="rounded-2xl border-2 border-sand-200 bg-white p-4">
      <h3 className="font-serif text-lg mb-3">Identity</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Conversation ID" value={identity.conversation_id} mono
               testId="diag-identity-conv-id" />
        <Field label="Phone Number ID" value={identity.phone_number_id} mono
               testId="diag-identity-pni" />
        <Field label="wa_id (masked)" value={identity.wa_id_masked} mono
               testId="diag-identity-wa-masked" />
        <Field label="Contact ID" value={identity.contact_id} mono />
        <Field label="Contact profile name" value={identity.contact_profile_name} />
        <Field label="Ultimo messaggio conv." value={fmtIso(identity.conv_last_message_at)} />
      </div>

      <div className="mt-4 rounded-xl bg-sand-50 border border-sand-200 p-3">
        <div className="text-xs text-ink-500 mb-1">CRM customer</div>
        {identity.crm_customer ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
            <div><span className="text-ink-500">Nome: </span>{identity.crm_customer.name || "—"}</div>
            <div><span className="text-ink-500">Telefono: </span><code>{identity.crm_customer.phone_masked}</code></div>
            <div><span className="text-ink-500">Status: </span>{identity.crm_customer.status || "—"}</div>
          </div>
        ) : (
          <div className="text-sm text-ink-500 italic">Nessun customer CRM associato.</div>
        )}
      </div>

      <div className="mt-4">
        <div className="text-xs text-ink-500 mb-1">
          Duplicate / legacy conversations · {identity.duplicates_count}
        </div>
        {(identity.duplicates_or_legacy || []).length === 0 ? (
          <div className="text-sm text-ink-500 italic" data-testid="diag-no-duplicates">
            Nessun duplicato rilevato.
          </div>
        ) : (
          <div className="space-y-1">
            {identity.duplicates_or_legacy.map((d) => (
              <div key={d._id}
                   data-testid="diag-dup-row"
                   className="text-xs font-mono bg-amber-50 border border-amber-300 rounded-lg px-2 py-1 flex justify-between">
                <span>{d._id}</span>
                <span>pni=<code>{d.phone_number_id}</code> wa=<code>{d.wa_id}</code> unread={d.unread_count ?? 0}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function OutboundSection({ items }) {
  return (
    <div data-testid="diag-outbound"
         className="rounded-2xl border-2 border-sand-200 bg-white p-4">
      <h3 className="font-serif text-lg mb-3">Ultimi outbound · {items?.length || 0}</h3>
      {(!items || items.length === 0) ? (
        <div className="text-sm text-ink-500 italic">Nessun outbound nella finestra.</div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {items.map((m) => (
            <div key={m._id}
                 data-testid="diag-outbound-row"
                 className="rounded-xl border border-sand-200 p-3 text-sm">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="text-xs text-ink-500">{fmtIso(m.created_at)}</span>
                <Chip tone={statusTone(m.last_status)} testId="diag-outbound-status">
                  {m.last_status || "n/d"}
                </Chip>
                <Chip>{m.send_source || m.origin || "—"}</Chip>
                <Chip>{m.type}</Chip>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                <div><span className="text-ink-500">wamid: </span><code>{m.id_meta || "—"}</code></div>
                <div><span className="text-ink-500">crid: </span><code>{m.client_request_id || "—"}</code></div>
                <div><span className="text-ink-500">body: </span>
                  {m.body_redacted
                    ? <code>«{m.body_redacted.head}…» (len={m.body_redacted.len})</code>
                    : "—"}
                </div>
                {m.automation_type && <div><span className="text-ink-500">automation: </span>{m.automation_type}</div>}
              </div>
              {(m.last_error_code || m.last_error_message
                 || (m.errors && m.errors.length)) && (
                <div className="mt-2 rounded-lg bg-red-50 border border-red-300 p-2 text-xs text-red-900">
                  <div className="font-semibold mb-0.5">Errore</div>
                  {m.last_error_code && <div>code: <code>{String(m.last_error_code)}</code></div>}
                  {m.last_error_title && <div>title: {m.last_error_title}</div>}
                  {m.last_error_message && <div>message: {m.last_error_message}</div>}
                  {m.last_error_data && (
                    <details className="mt-1">
                      <summary className="cursor-pointer">error_data</summary>
                      <pre className="text-[10px] whitespace-pre-wrap">{JSON.stringify(m.last_error_data, null, 2)}</pre>
                    </details>
                  )}
                  {m.errors && m.errors.length > 0 && (
                    <details className="mt-1">
                      <summary className="cursor-pointer">errors[]</summary>
                      <pre className="text-[10px] whitespace-pre-wrap">{JSON.stringify(m.errors, null, 2)}</pre>
                    </details>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReservationsSection({ items }) {
  return (
    <div data-testid="diag-reservations"
         className="rounded-2xl border-2 border-sand-200 bg-white p-4">
      <h3 className="font-serif text-lg mb-3">Send reservations · {items?.length || 0}</h3>
      {(!items || items.length === 0) ? (
        <div className="text-sm text-ink-500 italic">Nessuna reservation correlata.</div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {items.map((r) => (
            <div key={r._id}
                 data-testid="diag-reservation-row"
                 className="rounded-xl border border-sand-200 p-3 text-sm">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="text-xs text-ink-500">{fmtIso(r.created_at)}</span>
                <Chip tone={statusTone(r.status)} testId="diag-reservation-status">
                  {r.status || "n/d"}
                </Chip>
                {r.pre_meta_failure === true && <Chip tone="red">pre_meta_failure</Chip>}
                {r.status === "delivery_unknown" && <Chip tone="amber">ambiguous</Chip>}
                <Chip>{r.send_source || "—"}</Chip>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                <div><span className="text-ink-500">crid: </span><code>{r.client_request_id || "—"}</code></div>
                <div><span className="text-ink-500">wamid: </span><code>{r.wamid || "—"}</code></div>
              </div>
              {(r.meta_error_code || r.meta_error_message) && (
                <div className="mt-2 rounded-lg bg-red-50 border border-red-300 p-2 text-xs text-red-900">
                  {r.meta_error_code && <div>meta_error_code: <code>{String(r.meta_error_code)}</code></div>}
                  {r.meta_error_message && <div>meta_error_message: {r.meta_error_message}</div>}
                  {r.meta_error_data && (
                    <details className="mt-1">
                      <summary className="cursor-pointer">meta_error_data</summary>
                      <pre className="text-[10px] whitespace-pre-wrap">{JSON.stringify(r.meta_error_data, null, 2)}</pre>
                    </details>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusEventsSection({ items }) {
  return (
    <div data-testid="diag-status-events"
         className="rounded-2xl border-2 border-sand-200 bg-white p-4">
      <h3 className="font-serif text-lg mb-3">Status webhook / Meta errors · {items?.length || 0}</h3>
      {(!items || items.length === 0) ? (
        <div className="text-sm text-ink-500 italic">
          Nessun status event con errori Meta.
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {items.map((ev) => (
            <div key={ev._id}
                 data-testid="diag-status-event-row"
                 className="rounded-xl border border-sand-200 p-3 text-sm">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="text-xs text-ink-500">{fmtIso(ev.received_at)}</span>
                <Chip tone={statusTone(ev.status)}>{ev.status}</Chip>
                <span className="text-xs font-mono">{ev.id_meta}</span>
              </div>
              {(ev.errors || []).map((e, i) => (
                <div key={i} className="mt-1 rounded-lg bg-red-50 border border-red-300 p-2 text-xs text-red-900">
                  <div>code: <code>{String(e.code)}</code></div>
                  {e.title && <div>title: {e.title}</div>}
                  {e.message && <div>message: {e.message}</div>}
                  {e.error_data && (
                    <details><summary className="cursor-pointer">error_data</summary>
                      <pre className="text-[10px] whitespace-pre-wrap">{JSON.stringify(e.error_data, null, 2)}</pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MediaSection({ items }) {
  return (
    <div data-testid="diag-media"
         className="rounded-2xl border-2 border-sand-200 bg-white p-4">
      <h3 className="font-serif text-lg mb-3">Media history · {items?.length || 0}</h3>
      {(!items || items.length === 0) ? (
        <div className="text-sm text-ink-500 italic">Nessun media nella storia.</div>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {items.map((m) => (
            <div key={m._id}
                 data-testid="diag-media-row"
                 className="rounded-xl border border-sand-200 p-2 text-xs flex flex-wrap items-center gap-2">
              <span className="text-ink-500">{fmtIso(m.created_at)}</span>
              <Chip>{m.type}</Chip>
              <Chip tone={m.direction === "in" ? "blue" : "neutral"}>{m.direction}</Chip>
              <code>{m.media?.id || "—"}</code>
              <span className="text-ink-500">{m.media?.mime_type || ""}</span>
              {m.media?._url_present && <Chip tone="amber">url_present</Chip>}
              {m.unsupported && <Chip tone="red">unsupported</Chip>}
              {m.errors && <Chip tone="red">errors</Chip>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ControlSection({ items }) {
  return (
    <div data-testid="diag-control"
         className="rounded-2xl border-2 border-sand-200 bg-white p-4">
      <h3 className="font-serif text-lg mb-3">
        Control: outbound OK verso altri destinatari · {items?.length || 0}
      </h3>
      {(!items || items.length === 0) ? (
        <div className="text-sm text-ink-500 italic">
          Nessun outbound riuscito recente verso altri contatti sullo stesso PNI.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((c) => (
            <div key={c._id}
                 data-testid="diag-control-row"
                 className="rounded-xl border border-sand-200 p-2 text-xs flex flex-wrap items-center gap-2">
              <span className="text-ink-500">{fmtIso(c.created_at)}</span>
              <Chip tone={statusTone(c.last_status)}>{c.last_status}</Chip>
              <Chip>{c.send_source || "—"}</Chip>
              <code>{c.wa_id_masked}</code>
              {c.phone_number_id_match ? (
                <Chip tone="green">stesso PNI</Chip>
              ) : (
                <Chip tone="amber">PNI diverso</Chip>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RawStatusErrorsSection({ items }) {
  return (
    <div data-testid="diag-raw-status-errors"
         className="rounded-2xl border-2 border-red-400 bg-red-50 p-4">
      <h3 className="font-serif text-lg mb-3">
        Raw Meta status errors · {items?.length || 0}
      </h3>
      <p className="text-xs text-red-800 mb-3">
        Estratti dal raw webhook Meta persistito, filtrati per
        <code> statuses[].id == id_meta</code>. Il payload raw completo
        NON è esposto — solo <code>errors[]</code> redatti + timestamp.
      </p>
      {(!items || items.length === 0) ? (
        <div className="text-sm text-ink-500 italic">
          Nessun raw status error trovato per gli outbound in finestra
          (nessun webhook Meta con errors ha citato questi wamid).
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {items.map((it, i) => (
            <div key={i}
                 data-testid="diag-raw-status-row"
                 className="rounded-xl border border-red-300 bg-white p-3 text-sm">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <Chip tone={statusTone(it.status)}>{it.status}</Chip>
                <code className="text-xs">{it.id_meta}</code>
                <span className="text-xs text-ink-500">
                  ts={it.timestamp} · recipient={it.recipient_id_masked || "—"}
                </span>
              </div>
              {(it.errors || []).map((e, j) => (
                <div key={j} className="mt-1 rounded-lg bg-red-100 border border-red-400 p-2 text-xs text-red-900">
                  <div>code: <code>{String(e.code)}</code></div>
                  {e.title && <div>title: {e.title}</div>}
                  {e.message && <div>message: {e.message}</div>}
                  {e.details && <div>details: {e.details}</div>}
                  {e.error_data && (
                    <details className="mt-1">
                      <summary className="cursor-pointer">error_data</summary>
                      <pre className="text-[10px] whitespace-pre-wrap">{JSON.stringify(e.error_data, null, 2)}</pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ChannelSection({ channel }) {
  if (!channel) return null;
  return (
    <div data-testid="diag-channel"
         className="rounded-2xl border-2 border-sand-200 bg-white p-4">
      <h3 className="font-serif text-lg mb-3">Channel</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Graph version" value={channel.graph_version} mono />
        <Field label="Config PNI" value={channel.config_phone_number_id} mono />
        <Field label="WABA / business_account_id" value={channel.config_business_account_id} mono />
        <Field label="Credential source" value={channel.credential_source} />
        <Field label="PNI match conv." value={String(channel.config_pni_matches_conv)} />
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Main
// -----------------------------------------------------------------------

export function ContactDiagnosticPanel({ presetConversationId } = {}) {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";

  const [convId, setConvId] = useState(presetConversationId || "");
  const [waId, setWaId] = useState("");
  const [fromTs, setFromTs] = useState("");
  const [toTs, setToTs] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    if (presetConversationId && presetConversationId !== convId) {
      setConvId(presetConversationId);
    }
  }, [presetConversationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = useCallback(async () => {
    setErr(null);
    if (!convId && !waId) {
      setErr("Serve conversation_id oppure wa_id.");
      return;
    }
    setLoading(true); setPayload(null);
    try {
      const params = {};
      if (convId) params.conversation_id = convId.trim();
      if (waId) params.wa_id = waId.trim();
      if (fromTs) params.from_ts = fromTs;
      if (toTs) params.to_ts = toTs;
      const { data } = await api.get(
        "/admin/messaging/whatsapp/contact-diagnostic", { params });
      setPayload(data);
    } catch (e) {
      setErr(formatApiError(e) || "Errore durante il diagnostico.");
    } finally {
      setLoading(false);
    }
  }, [convId, waId, fromTs, toTs]);

  const copyJson = useCallback(async () => {
    if (!payload) return;
    try {
      const text = JSON.stringify(payload, null, 2);
      await navigator.clipboard.writeText(text);
      toast.success("JSON diagnostica copiato negli appunti.");
    } catch {
      toast.error("Impossibile copiare (browser).");
    }
  }, [payload]);

  if (!isSuper) return null;

  return (
    <section data-testid="contact-diagnostic-panel"
             className="mt-8 rounded-3xl border-2 border-red-300 bg-red-50/40 p-6">
      <h2 className="font-serif text-2xl mb-1">
        Diagnostica contatto WhatsApp · Super Admin
      </h2>
      <p className="text-xs text-ink-500 mb-4">
        READ-ONLY. Riusa l'autenticazione dell'app. Nessun invio, nessuna
        modifica, nessuna cancellazione. Endpoint:
        <code> GET /api/admin/messaging/whatsapp/contact-diagnostic</code>.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <label className="text-xs">
          Conversation ID
          <input data-testid="diag-input-conv-id"
                 value={convId}
                 onChange={(e) => setConvId(e.target.value)}
                 placeholder="ObjectId (24 hex)"
                 className="w-full mt-1 rounded-lg border border-sand-300 px-2 py-1.5 text-sm font-mono" />
        </label>
        <label className="text-xs">
          wa_id (E.164 digits, senza +)
          <input data-testid="diag-input-wa-id"
                 value={waId}
                 onChange={(e) => setWaId(e.target.value)}
                 placeholder="es. 393xxxxxxxxx"
                 className="w-full mt-1 rounded-lg border border-sand-300 px-2 py-1.5 text-sm font-mono" />
        </label>
        <label className="text-xs">
          From (ISO UTC, opzionale)
          <input data-testid="diag-input-from"
                 value={fromTs}
                 onChange={(e) => setFromTs(e.target.value)}
                 placeholder="2026-09-14T19:00:00Z"
                 className="w-full mt-1 rounded-lg border border-sand-300 px-2 py-1.5 text-sm font-mono" />
        </label>
        <label className="text-xs">
          To (ISO UTC, opzionale)
          <input data-testid="diag-input-to"
                 value={toTs}
                 onChange={(e) => setToTs(e.target.value)}
                 placeholder="2026-09-14T20:30:00Z"
                 className="w-full mt-1 rounded-lg border border-sand-300 px-2 py-1.5 text-sm font-mono" />
        </label>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <button data-testid="diag-run-btn"
                type="button" disabled={loading}
                onClick={run}
                className="btn-primary disabled:opacity-40">
          {loading ? "Analisi in corso…" : "Esegui diagnostica"}
        </button>
        {payload && (
          <button data-testid="diag-copy-json-btn"
                  type="button"
                  onClick={copyJson}
                  className="btn-secondary">
            Copia JSON diagnostica
          </button>
        )}
      </div>

      {err && (
        <div data-testid="diag-error"
             className="rounded-xl border-2 border-red-300 bg-red-50 p-3 text-sm text-red-900 mb-4">
          {err}
        </div>
      )}

      {payload && (
        <div className="space-y-4">
          <div className="text-xs text-ink-500" data-testid="diag-window">
            Finestra: <code>{payload.window?.from}</code> → <code>{payload.window?.to}</code>
            &nbsp;· generated_at: <code>{payload.generated_at}</code>
          </div>
          <IdentitySection identity={payload.identity} />
          <OutboundSection items={payload.outbound_messages} />
          <ReservationsSection items={payload.send_reservations} />
          <StatusEventsSection items={payload.status_events} />
          <RawStatusErrorsSection items={payload.raw_status_errors} />
          <MediaSection items={payload.media_history} />
          <ControlSection items={payload.control_contact_outbound_ok} />
          <ChannelSection channel={payload.channel} />
        </div>
      )}
    </section>
  );
}

export default ContactDiagnosticPanel;
